import { useEffect, useMemo, useState } from 'react'
import { useDirty } from '@/hooks/useDirty'
import {
  Banknote,
  Calculator,
  Download,
  Eye,
  Landmark,
  Pencil,
  Plus,
  Save,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react'
import { downloadSchedulePdf, useReportBrand } from '@/lib/pdf'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { formatDate } from '@/lib/locale'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { StatCard } from '@/components/StatCard'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useCoa } from '@/features/coa/queries'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import {
  useCreateLoan,
  useDeleteLoan,
  useLoanDetail,
  useLoanSchedules,
  useUpdateLoan,
} from '@/features/loan/queries'
import { EditScheduleDialog } from '@/components/EditScheduleDialog'
import { buildLoanSchedule } from '@/features/loan/calc'
import type {
  CreateLoanPayload,
  LoanEntry,
  LoanPreviewSummary,
  LoanSchedule,
} from '@/features/loan/types'
import type { TaxonomyItem } from '@/features/coa/types'

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(n)
}

function taxOption(t: TaxonomyItem): ComboboxOption<string> {
  return {
    value: t.account_code,
    label: `${t.account_code} - ${t.subcategory_name}`,
    description: t.main_category,
  }
}

type FormState = {
  lender: string
  description: string
  amount: string
  rate: string
  years: string
  paymentsPerYear: string
  startDate: string
  liabilityAccount: string // credit_account (loan / notes payable, B/S)
  accruedInterest: string // debit_account (accrued interest, B/S)
  interestExpense: string // third_account (interest expense, I/S)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function defaultForm(): FormState {
  return {
    lender: '',
    description: '',
    amount: '',
    rate: '',
    years: '',
    paymentsPerYear: '12',
    startDate: todayIso(),
    liabilityAccount: '',
    accruedInterest: '',
    interestExpense: '',
  }
}

export function LoanAmortizationPage() {
  const { toast } = useToast()
  const coa = useCoa()
  const schedules = useLoanSchedules()
  const posting = usePostingStatus()
  const createMut = useCreateLoan()
  const updateMut = useUpdateLoan()
  const deleteMut = useDeleteLoan()
  const postSelectedMut = usePostSelected()

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<FormState>(() => defaultForm())
  const [preview, setPreview] = useState<LoanEntry[]>([])
  const [summary, setSummary] = useState<LoanPreviewSummary | null>(null)
  const [formErr, setFormErr] = useState<string | null>(null)
  const [viewId, setViewId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LoanSchedule | null>(null)
  const [editTarget, setEditTarget] = useState<LoanSchedule | null>(null)
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)

  const rows = useMemo(() => schedules.data ?? [], [schedules.data])

  const isAccountOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Income Statement')
      .map(taxOption)
  }, [coa.data])

  const bsAccountOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Balance Sheet')
      .map(taxOption)
  }, [coa.data])

  const stats = useMemo(() => {
    const totalOutstanding = rows.reduce(
      (sum, s) => sum + (parseFloat(String(s.initial_amount ?? 0)) || 0),
      0,
    )
    return { count: rows.length, totalOutstanding }
  }, [rows])

  // Why: posting.data.unpostedSchedules is the sum across depreciation +
  // prepaid + loan schedules. Read only the loan bucket so the banner on
  // this page matches its own scope.
  const unpostedNum = posting.data?.unpostedSchedulesByType?.loan ?? 0

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // Dirty-check the create-loan modal. Same pattern as Prepaid /
  // FixedAssets - empty defaultForm() baseline so any field change
  // unlocks Save. Edits live in EditLoanModal which owns its own
  // dirty tracking.
  const formBaseline = useMemo(() => defaultForm(), [])
  const dirty = useDirty(form, formBaseline)

  const handleCalculate = () => {
    setFormErr(null)
    const loanAmount = parseFloat(form.amount)
    const annualRate = parseFloat(form.rate)
    const loanYears = parseFloat(form.years)
    const paymentsPerYear = parseInt(form.paymentsPerYear, 10)
    if (
      !(loanAmount > 0) ||
      !(annualRate > 0) ||
      !(loanYears > 0) ||
      !(paymentsPerYear > 0) ||
      !form.startDate
    ) {
      setFormErr('Fill in loan amount, interest rate, period, payments per year, and start date.')
      setPreview([])
      setSummary(null)
      return
    }
    const { entries, summary } = buildLoanSchedule({
      loanAmount,
      annualRate,
      loanYears,
      paymentsPerYear,
      startDate: form.startDate,
    })
    setPreview(entries)
    setSummary(summary)
  }

  const handleSave = async () => {
    if (preview.length === 0 || !summary) {
      setFormErr('Calculate the schedule first.')
      return
    }
    if (!form.liabilityAccount) {
      setFormErr('Loan / Notes Payable (B/S) is required.')
      return
    }
    if (!form.interestExpense) {
      setFormErr('Interest Expense (I/S) is required.')
      return
    }
    const payload: CreateLoanPayload = {
      schedule_type: 'loan',
      description: form.description,
      category: 'Loan',
      initial_amount: parseFloat(form.amount),
      start_date: form.startDate,
      purchase_date: null,
      duration_periods: preview.length,
      params: {
        lender_name: form.lender,
        annual_interest_rate: parseFloat(form.rate),
        loan_period_years: parseFloat(form.years),
        payments_per_year: parseInt(form.paymentsPerYear, 10),
      },
      debit_account: form.accruedInterest,
      credit_account: form.liabilityAccount,
      third_account: form.interestExpense,
      entries: preview,
    }
    try {
      await createMut.mutateAsync(payload)
      toast({ title: 'Loan schedule saved.' })
      setFormOpen(false)
      setForm(defaultForm())
      setPreview([])
      setSummary(null)
    } catch (err) {
      setFormErr(errorMessage(err))
    }
  }

  const runDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync(deleteTarget.id)
      toast({ title: 'Loan voided.' })
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Void failed',
        description: errorMessage(err),
      })
    }
  }

  const fmtMoneyLocal = (v: number | string | null | undefined) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
    return formatCurrencyPrecise(isFinite(n) ? n : 0)
  }

  const loanItems = useMemo<PostingSelectorItem[]>(() => {
    const rows = unpostedPreview.data?.schedules ?? []
    return rows
      .filter((r) => r.scheduleType === 'loan')
      .map((r) => ({
        id: r.id,
        cells: [
          formatDate(r.entryDate),
          <span className="block max-w-[320px] truncate" title={r.scheduleDescription ?? undefined}>
            {r.scheduleDescription || r.category || 'Loan'}
          </span>,
          `Period ${r.periodNumber}`,
          <span className="text-xs text-muted-foreground">
            Interest {r.thirdAccount || '-'} / Cr {r.debitAccount || '-'}
          </span>,
          fmtMoneyLocal(r.secondaryAmount || r.amount),
        ],
        blocked: r.blocked,
        blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
      }))
  }, [unpostedPreview.data])

  const loanColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Description' },
    { header: 'Period', width: 'w-24' },
    { header: 'Accounts' },
    { header: 'Amount', align: 'right' as const, width: 'w-28' },
  ]

  const runPost = async (selection: Record<string, number[]>) => {
    const ids = selection.loan ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'loan', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? ids.length} loan entr${(r.posted ?? ids.length) === 1 ? 'y' : 'ies'}.`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: errorMessage(err),
      })
    }
  }

  if (schedules.isLoading || coa.isLoading) {
    return (
      <Page title="Loan amortization">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (schedules.isError) {
    return (
      <Page title="Loan amortization">
        <Alert variant="destructive">
          <AlertDescription>
            Could not load schedules: {errorMessage(schedules.error)}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  const totalInterestFromPreview = summary?.totalInterest ?? 0

  return (
    <Page
      title="Loan amortization"
      description="Build loan repayment schedules with interest and principal splits, then post the interest accrual entries."
      width="wide"
      actions={
        <Button onClick={() => setFormOpen((v) => !v)}>
          {formOpen ? <X className="size-4" /> : <Plus className="size-4" />}
          {formOpen ? 'Close form' : 'New loan'}
        </Button>
      }
    >
      <PostingBanner
        count={unpostedNum}
        label="loan payment entries"
        singularLabel="loan payment entry"
        source="loan"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending loan entries' : 'Post loan amortization entries'}
        description={selectorReadOnly
          ? 'These loan entries are queued to post.'
          : 'Tick the entries you want to post. Posting only affects the rows you select.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={[
          {
            key: 'loan',
            label: 'Loan',
            items: loanItems,
            columns: loanColumns,
            emptyMessage: 'No loan entries are pending.',
          },
        ]}
        onPost={runPost}
      />

      {/* Why: removed the "Unposted" stat - the Post Now banner above
          already shows the unposted count. Same change applied to the
          Accruals, Depreciation, and Prepaid pages. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Active loans" value={stats.count} icon={Landmark} />
        <StatCard
          label="Total outstanding"
          value={fmtMoney(stats.totalOutstanding)}
          icon={Banknote}
        />
        <StatCard
          label="Total interest (preview)"
          value={fmtMoney(totalInterestFromPreview)}
          icon={TrendingUp}
          hint={summary ? 'from current preview' : undefined}
        />
      </div>

      {formOpen ? (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <FormSection title="Loan details">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <FormField
                  label="Lender name"
                  placeholder="e.g. Bank of America"
                  value={form.lender}
                  onChange={(e) => update('lender', e.currentTarget.value)}
                />
                <FormField
                  label="Loan description"
                  placeholder="e.g. Equipment loan"
                  value={form.description}
                  onChange={(e) => update('description', e.currentTarget.value)}
                />
                <FormField
                  label="Loan amount ($)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => update('amount', e.currentTarget.value)}
                />
                <FormField
                  label="Annual interest rate (%)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="e.g. 5.5"
                  value={form.rate}
                  onChange={(e) => update('rate', e.currentTarget.value)}
                />
                <FormField
                  label="Loan period (years)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="e.g. 5"
                  value={form.years}
                  onChange={(e) => update('years', e.currentTarget.value)}
                />
                <FormField
                  label="Payments per year"
                  type="number"
                  min="1"
                  placeholder="12"
                  value={form.paymentsPerYear}
                  onChange={(e) => update('paymentsPerYear', e.currentTarget.value)}
                />
                <FormField
                  label="Start date of loan"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update('startDate', e.currentTarget.value)}
                />
              </div>
            </FormSection>

            <FormSection title="GL accounts">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Loan / Notes payable (B/S) <span className="text-destructive">*</span>
                  </label>
                  <Combobox
                    options={bsAccountOptions}
                    value={form.liabilityAccount || null}
                    placeholder="Search liability account…"
                    onChange={(v) => update('liabilityAccount', v ?? '')}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Accrued interest (B/S)</label>
                  <Combobox
                    options={bsAccountOptions}
                    value={form.accruedInterest || null}
                    placeholder="Search accrued interest account…"
                    onChange={(v) => update('accruedInterest', v ?? '')}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Interest expense (I/S) <span className="text-destructive">*</span>
                  </label>
                  <Combobox
                    options={isAccountOptions}
                    value={form.interestExpense || null}
                    placeholder="Search interest expense account…"
                    onChange={(v) => update('interestExpense', v ?? '')}
                  />
                </div>
              </div>
            </FormSection>

            {formErr ? (
              <Alert variant="destructive">
                <AlertDescription>{formErr}</AlertDescription>
              </Alert>
            ) : null}

            <Toolbar align="end">
              <Button variant="secondary" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline" onClick={handleCalculate}>
                <Calculator className="size-4" /> Calculate preview
              </Button>
              {preview.length > 0 ? (
                <Button onClick={handleSave} disabled={!dirty || createMut.isPending}>
                  <Save className="size-4" /> Save schedule
                </Button>
              ) : null}
            </Toolbar>

            {summary && preview.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-md bg-info/10 p-4 text-sm sm:grid-cols-2">
                  <SummaryItem label="Scheduled payment" value={fmtMoney(summary.scheduledPayment)} />
                  <SummaryItem label="Total payments" value={summary.totalPayments} />
                  <SummaryItem label="Total interest" value={fmtMoney(summary.totalInterest)} />
                  <SummaryItem label="Total cost of loan" value={fmtMoney(summary.totalCost)} />
                </div>
                <h3 className="mb-2 text-sm font-semibold">Amortization preview</h3>
                <DataTable
                  sortable={false}
                  rows={preview.map((e, i) => ({
                    ...e,
                    begin_balance:
                      i === 0
                        ? parseFloat(form.amount)
                        : preview[i - 1].balance,
                    scheduled_payment: summary.scheduledPayment,
                  }))}
                  columns={[
                    {
                      header: () => <span className="block text-center">Pmt #</span>,
                      accessorKey: 'period_number',
                      cell: ({ row }) => (
                        <span className="block text-center text-xs text-muted-foreground">
                          {row.original.period_number}
                        </span>
                      ),
                    },
                    {
                      header: 'Payment date',
                      accessorKey: 'entry_date',
                      cell: ({ row }) => (
                        <span className="whitespace-nowrap">
                          {new Date(row.original.entry_date).toLocaleDateString()}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Beginning</span>,
                      accessorKey: 'begin_balance',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.begin_balance)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Payment</span>,
                      accessorKey: 'scheduled_payment',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.scheduled_payment)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Principal</span>,
                      accessorKey: 'amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.amount)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Interest</span>,
                      accessorKey: 'secondary_amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.secondary_amount)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Ending</span>,
                      accessorKey: 'balance',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.balance)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Cum. interest</span>,
                      accessorKey: 'accumulated',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.accumulated)}
                        </span>
                      ),
                    },
                  ]}
                />
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h2 className="text-base font-semibold">Saved schedules</h2>
          {rows.length === 0 ? (
            <EmptyState
              icon={Landmark}
              title="No loan schedules yet"
              description="Click New loan to create your first amortization schedule."
              action={
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="size-4" /> Create schedule
                </Button>
              }
            />
          ) : (
            <>
              <div className="hidden md:block">
                <DataTable
                  sortable={false}
                  rows={rows}
                  columns={[
                    {
                      header: 'Lender',
                      accessorKey: 'params',
                      cell: ({ row }) =>
                        (row.original.params as { lender_name?: string } | null)?.lender_name ||
                        '-',
                    },
                    { header: 'Description', accessorKey: 'description' },
                    {
                      header: () => <span className="block text-right">Loan amount</span>,
                      accessorKey: 'initial_amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.initial_amount)}
                        </span>
                      ),
                    },
                    {
                      header: 'Rate',
                      accessorKey: 'params',
                      id: 'rate',
                      cell: ({ row }) =>
                        `${
                          (row.original.params as { annual_interest_rate?: number } | null)
                            ?.annual_interest_rate ?? 0
                        }%`,
                    },
                    {
                      header: 'Term',
                      accessorKey: 'params',
                      id: 'term',
                      cell: ({ row }) =>
                        `${
                          (row.original.params as { loan_period_years?: number } | null)
                            ?.loan_period_years ?? 0
                        } yrs`,
                    },
                    {
                      header: 'Start',
                      accessorKey: 'start_date',
                      cell: ({ row }) =>
                        row.original.start_date
                          ? new Date(row.original.start_date).toLocaleDateString()
                          : '-',
                    },
                    {
                      header: 'Status',
                      accessorKey: 'status',
                      cell: ({ row }) => (
                        <Badge variant={row.original.status === 'active' ? 'success' : 'secondary'}>
                          {row.original.status}
                        </Badge>
                      ),
                    },
                    {
                      id: 'actions',
                      header: () => <span className="block text-right">Actions</span>,
                      cell: ({ row }) => (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setViewId(row.original.id)}
                            title="View details"
                          >
                            <Eye className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditTarget(row.original)}
                            title="Edit details"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteTarget(row.original)}
                            title="Void schedule"
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
              <div className="space-y-3 md:hidden">
                {rows.map((s) => (
                  <LoanCard
                    key={s.id}
                    schedule={s}
                    onView={() => setViewId(s.id)}
                    onEdit={() => setEditTarget(s)}
                    onDelete={() => setDeleteTarget(s)}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ViewLoanModal id={viewId} onClose={() => setViewId(null)} />

      <EditScheduleDialog
        open={editTarget !== null}
        onOpenChange={(v) => !v && setEditTarget(null)}
        thirdAccountLabel="Interest expense account"
        schedule={editTarget ? {
          id: editTarget.id,
          description: editTarget.description,
          category: editTarget.category,
          debit_account: editTarget.debit_account,
          credit_account: editTarget.credit_account,
          third_account: editTarget.third_account,
        } : null}
        busy={updateMut.isPending}
        onSubmit={async (id, body) => {
          try {
            await updateMut.mutateAsync({ id, body: body as Partial<CreateLoanPayload> })
            toast({ title: 'Schedule updated.' })
            setEditTarget(null)
          } catch (err) {
            toast({ variant: 'destructive', title: 'Update failed', description: errorMessage(err) })
          }
        }}
      />

      <Modal.Confirm
        open={deleteTarget !== null}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Void loan schedule?"
        body={
          <p>
            This will void the loan schedule and create reversing entries for any periods already
            posted to the general ledger. Continue?
          </p>
        }
        confirmLabel="Void schedule"
        onConfirm={runDelete}
        onCancel={() => setDeleteTarget(null)}
        busy={deleteMut.isPending}
      />

    </Page>
  )
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 border-b border-primary pb-1 text-sm font-semibold uppercase tracking-wide text-foreground">
        {title}
      </h3>
      {children}
    </section>
  )
}

function SummaryItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function LoanCard({
  schedule,
  onView,
  onEdit,
  onDelete,
}: {
  schedule: LoanSchedule
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const params = schedule.params as
    | { lender_name?: string; annual_interest_rate?: number; loan_period_years?: number }
    | null
    | undefined
  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{params?.lender_name || 'Lender'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {schedule.description || '-'}
          </p>
        </div>
        <Badge variant={schedule.status === 'active' ? 'success' : 'secondary'}>
          {schedule.status}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span>
          Amount: <strong>{fmtMoney(schedule.initial_amount)}</strong>
        </span>
        <span>Rate: {params?.annual_interest_rate ?? 0}%</span>
        <span>Term: {params?.loan_period_years ?? 0} yrs</span>
      </div>
      <Toolbar align="end">
        <Button size="sm" variant="ghost" onClick={onView}>
          <Eye className="size-4" /> View
        </Button>
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Pencil className="size-4" /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="size-4 text-destructive" /> Void
        </Button>
      </Toolbar>
    </div>
  )
}

function ViewLoanModal({ id, onClose }: { id: number | null; onClose: () => void }) {
  const detail = useLoanDetail(id)
  const schedule = detail.data?.schedule
  const entries = detail.data?.entries ?? []
  const brand = useReportBrand()

  useEffect(() => {
    if (id === null) return
  }, [id])

  const params = schedule?.params as
    | { lender_name?: string; annual_interest_rate?: number; loan_period_years?: number }
    | null
    | undefined

  const onDownloadPdf = () => {
    if (!schedule) return
    downloadSchedulePdf('loan', schedule, entries, brand)
  }

  return (
    <Modal
      open={id !== null}
      onOpenChange={(v) => !v && onClose()}
      title={
        schedule
          ? `${params?.lender_name ?? 'Loan'} - ${schedule.description ?? 'schedule'}`
          : 'Loan schedule'
      }
      description={
        schedule
          ? `${fmtMoney(schedule.initial_amount)} at ${params?.annual_interest_rate ?? 0}% for ${params?.loan_period_years ?? 0} years`
          : undefined
      }
      className="max-w-3xl"
    >
      {detail.isLoading ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : detail.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load schedule.</AlertDescription>
        </Alert>
      ) : entries.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No entries.</p>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <DataTable
            sortable={false}
            rows={entries}
            columns={[
              {
                header: () => <span className="block text-center">#</span>,
                accessorKey: 'period_number',
                cell: ({ row }) => (
                  <span className="block text-center text-xs text-muted-foreground">
                    {row.original.period_number}
                  </span>
                ),
              },
              {
                header: 'Date',
                accessorKey: 'entry_date',
                cell: ({ row }) => (
                  <span className="whitespace-nowrap">
                    {new Date(row.original.entry_date).toLocaleDateString()}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Principal</span>,
                accessorKey: 'amount',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.amount)}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Interest</span>,
                accessorKey: 'secondary_amount',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.secondary_amount ?? 0)}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Balance</span>,
                accessorKey: 'balance',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.balance)}
                  </span>
                ),
              },
              {
                header: 'Posted',
                accessorKey: 'posted',
                cell: ({ row }) => (
                  <Badge variant={row.original.posted ? 'success' : 'secondary'}>
                    {row.original.posted ? 'Yes' : 'No'}
                  </Badge>
                ),
              },
            ]}
          />
        </div>
      )}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button
          variant="secondary"
          onClick={onDownloadPdf}
          disabled={!schedule || entries.length === 0}
        >
          <Download className="size-4" />
          Download PDF
        </Button>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  )
}
